import { useEffect, useMemo, useState } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { FormControl, MenuItem, Select, SelectChangeEvent, Stack, styled } from '@mui/material';
import { isEmpty } from '@npi/buddy-utils';
import { useField, useFormikContext } from 'formik';

export const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
export const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface SelectBaseProps {
    name: string;
    label: string;
    inputLabel: string;
    menuItems: { key: string; value: string }[];
    initialValue?: string;
    setSelectedValue?: (value: string) => void;
    disabled?: boolean;
    dataTestId?: string;
}

const TermSelect = styled(Select)(({ error }) => {
    return {
        border: 'thin solid #f3a256 !important',
        borderRadius: '8px',
        minWidth: '100px',
        marginTop: '5px',
        boxShadow: 'none',
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
        '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            border: 0,
        },
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 0,
        },
        '&:hover': {
            border: '2px solid #f3a256 !important',
        },
        ...(error && {
            border: '2px solid #f06c62 !important',
            '&:hover': {
                border: '2px solid #f06c62 !important',
            },
        }),
    };
});

export const SelectBaseField = ({
    label,
    inputLabel,
    menuItems: initialMenuItems,
    initialValue = '',
    setSelectedValue = (value: string) => {
        return value;
    },
    name,
    ...props
}: SelectBaseProps) => {
    const { setFieldValue } = useFormikContext();
    const [field, meta] = useField(name);
    const [defaultLabel, setDefaultLabel] = useState(initialValue);
    const [showError, setShowError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        if (meta.touched) {
            setShowError(true);
            setErrorMessage(meta.error);
            if (field.value) {
                setShowError(false);
                setErrorMessage('');
            }
        }
    }, [meta.touched, field.name, field.value, showError, meta.error]);

    const handleChange = (event: SelectChangeEvent) => {
        const { value } = event.target;
        setDefaultLabel(value);
        setFieldValue(name, value);
        setSelectedValue(value);
    };

    const menuItems = useMemo(() => {
        const items = [...initialMenuItems];
        if (!defaultLabel && initialValue) {
            return [...items].filter((option) => {
                return option.key !== initialValue;
            });
        }
        return items;
    }, [defaultLabel, initialMenuItems, initialValue]);

    return (
        <Stack sx={{ position: 'relative' }}>
            <label className="mb-5 font-bold">{label}</label>
            <FormControl>
                <TermSelect
                    {...field}
                    {...props}
                    value={defaultLabel}
                    displayEmpty
                    onChange={handleChange}
                    renderValue={(value) => {
                        if (isEmpty(value)) {
                            return <span style={{ opacity: 0.6 }}>{inputLabel}</span>;
                        }
                        return menuItems.find((item) => {
                            return item?.key === value;
                        })?.value;
                    }}
                    error={showError}
                >
                    <MenuItem disabled value="">
                        {inputLabel}
                    </MenuItem>
                    {menuItems.map((item: { key: string; value: string }, index: number) => {
                        return (
                            <MenuItem key={index} value={item.key}>
                                {item.value}
                            </MenuItem>
                        );
                    })}
                </TermSelect>
            </FormControl>
            {showError && <label className="input-base-field-error">{errorMessage}</label>}
        </Stack>
    );
};

export const SelectBase = ({ inputLabel, menuItems, custom = false, ...props }) => {
    return (
        <Select
            displayEmpty
            className={!custom ? 'selectbase-input' : ''}
            renderValue={(value) => {
                if (isEmpty(value)) {
                    return <span style={{ opacity: 0.6 }}>{inputLabel}</span>;
                }
                return menuItems.find((item) => {
                    return item?.key === value;
                })?.value;
            }}
            placeholder={inputLabel}
            inputProps={{
                'data-testid': `${props.name}-select`,
            }}
            {...props}
        >
            {menuItems.map((item: any, index: number) => {
                return (
                    <MenuItem key={index} value={item.key}>
                        {item.value}
                    </MenuItem>
                );
            })}
        </Select>
    );
};
